import { tabbable } from 'tabbable';
import { timeoutFocus } from './DomUtil';

export interface DisableAutoFocus {
  /**
  * ## PLEASE DISCUSS YOUR SCENARIO IN #dev-shared-widgets BEFORE USING THIS PROP.
  *
  * By default this dialog focuses the first focusable element within its content upon mount.
  * This is an accessibility requirement, so it should be **very rare** for this prop to be set to `true`.
  *
  * These examples uses `DialogWidget`, but it would work the same for any other widget using this interface as well.
  *
  * To instead customize which element (inside the dialog) should be auto-focused, provide a callback.
  * Typically this is done via a ref:
  * ```
  *   const ComponentThatRendersADialog = () => {
  *     const interestingElementRef = React.useRef<HTMLElement>(null);
  *
  *     return <DialogWidget disableAutoFocus={() => interestingElementRef.current!}>
  *       <ButtonWidget>A button, but not one that we want initially focused</ButtonWidget>
  *       <TextAreaWidget ref={interestingElementRef}>This is the thing we want initially focused</TextAreaWidget>
  *     </Widget>
  *   }
  * ```
  *
  * If a particular scenario makes the use of a ref complicated, the provided `dialogElement` can instead be queried. The following
  * example queries on the tag name, but more realistic examples might query on id, className, or some other convenient attribute data.
  * ```
  *   const ComponentThatQueriesTheDialogInstead = () => {
  *     return <DialogWidget disableAutoFocus={dialogElement => dialogElement.querySelector<HTMLElement>('textarea')}>
  *       <ButtonWidget>A button, but not one that we want initially focused</ButtonWidget>
  *       <TextAreaWidget>This is the thing we want initially focused</TextAreaWidget>
  *     </DialogWidget>
  *   }
  * ```
  */
  disableAutoFocus?: boolean | ((element: HTMLElement) => HTMLElement);
}

/**
 *
 * @param disableAutoFocus The value from the prop
 * @param container The container to query for focusable elements
 * @param shiftFirstElement A `true` value, will take the first focusable element and move it to the end of the stack.
 * In almost all cases, this will be for when the first focusable element is a close button.
 */
export function autoFocus(disableAutoFocus: DisableAutoFocus['disableAutoFocus'] | undefined, container: HTMLElement, shiftFirstElement: boolean): void {
  if (disableAutoFocus === true) {
    return;
  }

  const getElementToFocus = typeof disableAutoFocus === 'function' ?
    disableAutoFocus :
    (widgetElement: HTMLElement) => {
      const tabbables = tabbable(widgetElement);
      if (tabbables.length === 0) {
        throw Error('Something within the widget must be focusable per a11y requirements');
      }

      // move the close button to the back of the line, i.e. only focus on it if there was nothing else focusable
      if (shiftFirstElement) {
        tabbables.push(tabbables.shift()!);
      }
      return tabbables[0];
    };

  const elementToFocus = getElementToFocus(container);
  if (!elementToFocus) {
    throw Error('No focusable element was returned by the `disableAutoFocus` callback');
  } else if (!container.contains(elementToFocus)) {
    throw Error(`Can't auto-focus an element outside of the widget`);
  }

  // Queue this so that it occurs after the floater or overlay is positioned,
  // so that when the browser scrolls to the focused element it will scroll to the correct position.
  // Also, a React component being mounted doesn't necessarily mean it is attached to the document;
  // gating behind a setTimeout is a usually-safe bet that the component has been attached.
  timeoutFocus(elementToFocus);
}
